import { getProgressPercentComplete, isProgressStarted } from 'phoenix2/utils/completion';
import { createSelector } from 'reselect';
import { GENERATION_STATUS_FAILED } from './actions';
import { TaskItemStatus } from '@sparx/grpc/lib/generated/apis/sparx/packages/v1/spxpkg_pb';

import {
    PACKAGE_LIST_KEY,
    PACKAGES_DATE_KEY,
    PACKAGE_DELETIONS_KEY,
    PACKAGES_KEY,
    PACKAGES_TYPES_KEY,
    TASK_ASSETS_KEY,
    TASKS_KEY,
} from './reducers';
import { PACKAGE_TYPE_PRACTICE } from './package-types';

export const getTasks = state => state.get(TASKS_KEY);

export const getTaskAssets = (state, taskId) => state.getIn([TASK_ASSETS_KEY, taskId]);

export const getPackage = (state, packageId) => state.getIn([PACKAGES_KEY, packageId]);

export const getPackageType = (state, packageId) =>
    state.getIn([PACKAGES_KEY, packageId, 'package_type']);

export const getPackageGenerationStatus = (state, packageId) =>
    state.getIn([PACKAGES_KEY, packageId, 'generation_status']);

export const getPackageState = (state, packageId) => state.getIn(['packageStates', packageId]);

export const getPackages = state => state.get(PACKAGES_KEY);

export const getPackageDeletions = state => state.get(PACKAGE_DELETIONS_KEY);

// Utility function for sorting a package list (i.e. a list of
// package ids) by most recent. If two packages have the same
// start date we use end date to sort them.
export const sortPkgListByRecent = (pkgs, pkgList) =>
    pkgList &&
    pkgs &&
    pkgList.sort((pkgIdA, pkgIdB) => {
        const pkgAStart = pkgs.getIn([pkgIdA, 'start_date'], '');
        const pkgBStart = pkgs.getIn([pkgIdB, 'start_date'], '');
        if (pkgAStart === pkgBStart) {
            const pkgAEnd = pkgs.getIn([pkgIdA, 'end_date'], '');
            const pkgBEnd = pkgs.getIn([pkgIdB, 'end_date'], '');
            return pkgBEnd.localeCompare(pkgAEnd);
        }
        return pkgBStart.localeCompare(pkgAStart);
    });

export const getPackagesByType = (state, packageType) =>
    getPackages(state).filter(aPackage => aPackage.get('package_type') === packageType);

export const getAllPackagesArePractice = state =>
    !getPackages(state).some(pkg => pkg.get('package_type') !== PACKAGE_TYPE_PRACTICE);

/**
 * getPkgsForStats gets packages which should be included in stats. These are packages which are not
 * failed and not cancelled.
 */
export const getPkgsForStats = state =>
    state
        .get(PACKAGES_KEY)
        .filter(
            p =>
                p.get('generation_status') !== GENERATION_STATUS_FAILED &&
                p.get('deleted_time') === '',
        );

export const getPackageStates = state => state.get('packageStates');

export const getPackagesDate = state => state.get(PACKAGES_DATE_KEY);

export const getPackageList = state => state.get(PACKAGE_LIST_KEY);

export const getPackageListByType = (state, packageType) => {
    const pkgs = getPackages(state);
    const pkgList = getPackageList(state);
    return pkgList && pkgList.filter(pkgId => pkgs.getIn([pkgId, 'package_type']) === packageType);
};

export const getPackagesTypes = state => state.get(PACKAGES_TYPES_KEY);

/**
 * Computes the status of a package from the package and package state.
 *
 * Status contains:
 *     - complete (whether the package is complete)
 *     - unstarted (whether the package has been started)
 *     - percentComplete (how complete the package is)
 *     - generationFailed (whether the package failed to generate)
 */
export const getPackageStatus = (pkg, pkgState) => {
    let complete = false;
    let unstarted = true;
    let generationFailed = false;
    let percentComplete = 0;

    if (pkg.get('generation_status') === GENERATION_STATUS_FAILED) {
        unstarted = false;
        generationFailed = true;
    } else if (pkgState) {
        complete = pkgState.get('complete');
        unstarted = !isProgressStarted(pkgState);
        percentComplete = getProgressPercentComplete(pkgState);
    }

    return {
        complete,
        unstarted,
        percentComplete,
        generationFailed,
    };
};

/**
 * Creates a selector that computes the package status from a package ID.
 */
export const makeSelectPackageStatus = () =>
    createSelector([getPackage, getPackageState], getPackageStatus);

// The below status enum looks to mirror what is sent by swworker
// not what status is mapped to on the client side.
export const SERVER_TASK_ITEM_STATUS = {
    UNATTEMPTED: 'UNATTEMPTED',
    DONE: 'DONE',
    WRONG: 'WRONG',
    SEEKHELP: 'SEEKHELP',
    BADGED: 'BADGED',
    HIDDEN: 'HIDDEN',
    WRONGWITHVIDEONUDGE: 'WRONGWITHVIDEONUDGE',
};

export const TASK_ITEM_STATUS = {
    done: 'done',
    wrong: 'wrong',
    seekHelp: 'seek-help',
    unattempted: 'unattempted',
    badged: 'badged',
    hidden: 'hidden',
    wrongWithVideoNudge: 'wrong-with-video-nudge',
};

export const statusMap = {
    [SERVER_TASK_ITEM_STATUS.UNATTEMPTED]: TASK_ITEM_STATUS.unattempted,
    [SERVER_TASK_ITEM_STATUS.DONE]: TASK_ITEM_STATUS.done,
    [SERVER_TASK_ITEM_STATUS.WRONG]: TASK_ITEM_STATUS.wrong,
    [SERVER_TASK_ITEM_STATUS.SEEKHELP]: TASK_ITEM_STATUS.seekHelp,
    [SERVER_TASK_ITEM_STATUS.BADGED]: TASK_ITEM_STATUS.badged,
    [SERVER_TASK_ITEM_STATUS.HIDDEN]: TASK_ITEM_STATUS.hidden,
    [SERVER_TASK_ITEM_STATUS.WRONGWITHVIDEONUDGE]: TASK_ITEM_STATUS.wrongWithVideoNudge,
    [TaskItemStatus.UNATTEMPTED]: TASK_ITEM_STATUS.unattempted,
    [TaskItemStatus.DONE]: TASK_ITEM_STATUS.done,
    [TaskItemStatus.WRONG]: TASK_ITEM_STATUS.wrong,
    [TaskItemStatus.WARNING]: TASK_ITEM_STATUS.wrong, // I dunno what that is
    [TaskItemStatus.SEEKHELP]: TASK_ITEM_STATUS.seekHelp,
    [TaskItemStatus.BADGED]: TASK_ITEM_STATUS.badged,
    [TaskItemStatus.HIDDEN]: TASK_ITEM_STATUS.hidden,
    [TaskItemStatus.WRONGWITHVIDEONUDGE]: TASK_ITEM_STATUS.wrongWithVideoNudge,
};

export const itemStatus = taskItemState => {
    if (!taskItemState) {
        // Not loaded yet
        return undefined;
    }
    const taskItemStatus = taskItemState.get('status');
    return statusMap[taskItemStatus] || TASK_ITEM_STATUS.unattempted;
};

export const getFilterStats = createSelector(
    [getPkgsForStats, getPackageStates],
    (packages, packageStates) => {
        const statsByType = {};
        for (const pkg of packages.values()) {
            const pkgType = pkg.get('package_type');
            let stats = statsByType[pkgType];
            if (!stats) {
                stats = {
                    numComplete: 0,
                    numNew: 0,
                    numTotal: 0,
                    stateAvailable: false,
                };
                statsByType[pkgType] = stats;
            }
            stats.numTotal++;
            const pkgState = packageStates.get(pkg.get('pkg_id'));
            if (pkgState) {
                stats.stateAvailable = true;
                const complete = pkgState.get('complete', false);
                const tasksDone = pkgState.get('tasksComplete') || 0;
                const tasksInProgress = pkgState.get('tasksInProgress') || 0;
                if (complete) {
                    stats.numComplete++;
                } else if (tasksDone === 0 && tasksInProgress === 0) {
                    stats.numNew++;
                }
            }
        }
        return statsByType;
    },
);
